import type {
  SearchCollectionsResponse,
  GetMyCollectionsResponse,
  GetCollectionResponse,
  CreateCollectionResponse,
  UpdateCollectionResponse,
  ArchiveCollectionResponse,
  RestoreCollectionResponse,
  AddPatternToCollectionResponse,
  RemovePatternFromCollectionResponse,
  FollowCollectionResponse,
  UnfollowCollectionResponse,
  CreateCollectionRequest,
  UpdateCollectionRequest,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE, API_BASE } from './helpers'

export async function searchCollections(
  query: string,
  filters: Record<string, string> = {},
  idToken?: string,
): Promise<SearchCollectionsResponse> {
  const params = new URLSearchParams({ q: query, ...filters })
  const url = `${API_BASE}/collections/search?${params}`
  const resp = await fetch(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  if (!resp.ok) throw new Error('Failed to search collections')
  return resp.json() as Promise<SearchCollectionsResponse>
}

export async function getUserCollections(minimal: boolean, idToken: string): Promise<GetMyCollectionsResponse> {
  const url = `${SECURE_API_BASE}/my-collections${minimal ? '?minimal=true' : ''}`
  return secureFetch(url, idToken, { method: 'GET' })
}

export async function getUserCollectionsPreview(
  isAuthenticated: boolean,
  idToken?: string,
): Promise<GetMyCollectionsResponse> {
  if (isAuthenticated && idToken) {
    return getUserCollections(true, idToken)
  }
  throw new Error('Not authenticated')
}

export async function getCollection(collectionId: string, idToken: string): Promise<GetCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}`, idToken, { method: 'GET' })
}

export async function updateCollection(
  collectionId: string,
  data: UpdateCollectionRequest,
  idToken: string,
): Promise<UpdateCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function archiveCollection(collectionId: string, idToken: string): Promise<ArchiveCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/archive`, idToken, {
    method: 'POST',
  })
}

export async function restoreCollection(collectionId: string, idToken: string): Promise<RestoreCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/restore`, idToken, {
    method: 'POST',
  })
}

export async function createCollection(
  data: CreateCollectionRequest,
  idToken: string,
): Promise<CreateCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function addPatternToCollection(
  collectionId: string,
  patternId: string,
  idToken: string,
): Promise<AddPatternToCollectionResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/patterns/${encodeURIComponent(patternId)}`,
    idToken,
    {
      method: 'POST',
    },
  )
}

export async function removePatternFromCollection(
  collectionId: string,
  patternId: string,
  idToken: string,
): Promise<RemovePatternFromCollectionResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/patterns/${encodeURIComponent(patternId)}`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

export async function followCollection(collectionId: string, idToken: string): Promise<FollowCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/follow`, idToken, {
    method: 'POST',
  })
}

export async function unfollowCollection(collectionId: string, idToken: string): Promise<UnfollowCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/follow`, idToken, {
    method: 'DELETE',
  })
}
